import { IntlShape } from 'react-intl'

import { addDays, isBefore, max } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { CountryCode, parsePhoneNumberFromString as parsePhoneNumber } from 'libphonenumber-js'
import {
  cloneDeep,
  get,
  includes,
  intersection,
  isArray,
  isBoolean,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isNumber,
  isString,
  merge,
  set,
} from 'lodash-es'
import moment from 'moment-timezone'

import {
  CLIENT_PROGRAM_DROPDOWN_DUMMY_EPISODE_ID,
  rcads25TeenAssessmentsDBTT,
  rcadsp25AssessmentsDBTT,
} from './constants'
import { customContentAttributes } from './customContentAttributes'
import {
  ActivityResponseData,
  AssessmentStages,
  AssignmentStatuses,
  AssignmentStatusesLabel,
  ClientProgramDropdownData,
  ClientProgramDropdownViewModel,
  DependentProgramDropdownData,
  VideoLesson,
} from './types'
import { Appointment } from '../../models/appointment/Appointment'
import {
  ActivityGroupType,
  Assignment,
  AssignmentGroup,
  AssignmentResponse,
  LessonContent,
  Response,
} from '../../models/assignments/Assignments'
import { ClientObject } from '../../models/client/Client'
import { EpisodeContents } from '../../models/episodes/Episodes'
import {
  Condition,
  FieldSchema,
  FieldValue,
  FormMetadata,
  SchemaProperties,
  UiMetadata,
} from '../../models/formMetadata/FormMetadata'
import { NoteMetadata } from '../../models/notes/Notes'
import { ProgramNames } from '../../models/programs/Programs'
import { Provider } from '../../models/providers/Providers'
import { getAppointmentDateTimeObject } from '../appointments/utils'
import {
  characterLimit,
  hasValue,
  isBooleanTrue,
  isPositiveInteger,
  requiredField as isRequired,
  isValid5DigitZipCode,
  isValidAge,
  isValidAgeCustomErrors,
  isValidCustomerCode,
  isValidEmail,
  isValidFirstName,
  validInternationalPhoneNumber as isValidInternationalPhoneNumber,
  isValidLastName,
  isValidPassword,
  isValidPastDate,
  isValidPastYear,
  validPhoneNumber as isValidPhoneNumber,
  isValidWalmartWin,
  maxValue,
  rangeHasValidValue,
  requireAtLeastOneOf,
} from '../common/utils/validationUtils'
import { getProviderSessionCounts } from '../providers/utils'

function isConditionMet(parentValue: FieldValue | FieldValue[] | undefined, answerValue: unknown): boolean {
  return isArray(parentValue)
    ? parentValue.some((pv) =>
        isArray(answerValue) ? (answerValue as Array<unknown>).includes(pv) : answerValue === pv,
      )
    : isEqual(answerValue, parentValue)
}

export function countConditionalPages(schema: FieldSchema, answers: ActivityResponseData | Dict) {
  const fields = (schema?.properties ?? {}) as Record<string, FieldSchema>

  // RunTime: O(fields.length)
  const filteredSchema = Object.keys(fields).reduce((acc: any, key) => {
    const field = fields[key]
    if (field?.condition?.fullPageIsConditional) {
      acc.push(field)
    }
    return acc
  }, [])

  const result: { totalCount: number; [key: string]: number } = { totalCount: 0 }

  // Prevent anymore computations
  if (!filteredSchema.length) return result

  let totalCount = 0

  // RunTime: O(answers.length)
  // Initialize result object with answers keys
  for (const key in answers) {
    if (Object.prototype.hasOwnProperty.call(answers, key)) {
      result[key] = 0
    }
  }

  // RunTime: O(filteredSchema.length)
  // Accumulate counts
  for (let i = 0; i < filteredSchema.length; i++) {
    const item = filteredSchema[i]
    const { parentField, parentValue } = item.condition

    if (Object.prototype.hasOwnProperty.call(answers, parentField)) {
      const value = (answers as Dict)[parentField]
      if (!isConditionMet(parentValue, value)) {
        result[parentField]++
        totalCount++
      }
    }
  }

  result.totalCount = totalCount
  return result
}

export function getHiddenConditionalPageNumbers(
  schema: FieldSchema,
  answers: ActivityResponseData | Dict,
): Set<number> {
  const fields = schema?.properties as Record<string, FieldSchema>
  const hiddenPages = new Set<number>()

  for (const key of Object.keys(fields ?? {})) {
    const field = fields[key]
    if (field?.condition?.fullPageIsConditional && isNumber(field.mobilePage)) {
      const { parentField, parentValue } = field.condition
      const value = (answers as Dict)[parentField as string]
      if (!isConditionMet(parentValue, value)) {
        hiddenPages.add(field.mobilePage)
      }
    }
  }

  return hiddenPages
}

/**
 * Determines if the activity/lesson is in the current activities.
 * Note: assessments/lessons with session_period 0s
 * do not follow session flow but have to be put in the past activities
 * when they are completed.
 * @param sessionPeriod Indicates what session period the activity has been assigned on
 * @param status Indicates the status of the activity
 * @param sessionCount Indicates the current session number for the client
 * @return boolean
 */

export const isActivityCurrentPeriod = (sessionPeriod: number, status: string, sessionCount: number) =>
  (sessionPeriod === 0 && status !== AssignmentStatuses.completed) ||
  (sessionPeriod === 0 && sessionCount === 1) ||
  sessionPeriod === sessionCount

/**
 * Determines if a post-care or final assessment has expired (more than 14 days old).
 *
 * IMPORTANT: This function handles two different types of assessments:
 * 1. Final assessments (stage = "end"): Expire 14 days after the episode ends
 * 2. Post-episode assessments (stage = "post_episode"): Expire 14 days after creation
 *    - Post-episode assessments can be sent 6, 9, or 12 months AFTER the episode ends
 *    - Using episodeEndDate for these would cause immediate expiration
 *
 * @param userTimeZone - The user's timezone for date comparison
 * @param postCareAssessment - The assessment to check
 * @param episodeEndDate - If provided AND assessment is stage "end", expiration is calculated from this date.
 *                         Otherwise falls back to create_date.
 * @returns true if the assessment has expired
 */
export const isPostCareAssessmentExpired = (
  userTimeZone: string,
  postCareAssessment: Assignment,
  episodeEndDate?: string | null,
) => {
  if (postCareAssessment?.status === AssignmentStatuses.completed) {
    return false
  }

  // Only use episodeEndDate for final assessments (stage = "end")
  // Post-episode assessments (stage = "post_episode") should always use create_date
  // because they can be sent months after the episode ends
  const shouldUseEpisodeEndDate =
    episodeEndDate && postCareAssessment?.content_meta_data?.stage === AssessmentStages.end

  const referenceDate = shouldUseEpisodeEndDate ? new Date(episodeEndDate) : new Date(postCareAssessment.create_date)
  const referenceDateInUserTz = utcToZonedTime(referenceDate, userTimeZone)
  const dateOfExpiration = addDays(referenceDateInUserTz, 14)
  const todayInUserTz = utcToZonedTime(new Date(), userTimeZone)
  return isBefore(dateOfExpiration, todayInUserTz)
}

/**
 * @param formatMessage
 * @param videos
 * @returns returns the title of cta in the lesson overview
 */

export const getCTATitle = (formatMessage: IntlShape['formatMessage'], videos: VideoLesson[]) => {
  if (videos.filter((video) => video.completionStatus === 'new').length !== videos.length) {
    return formatMessage({
      defaultMessage: 'Continue',
      description: 'title of the button to continue video lesson',
    })
  }
  return formatMessage({
    defaultMessage: 'Start lesson',
    description: 'title of the button to start video lesson',
  })
}

/**
 *
 * @param videos
 * @returns returns the first index of incomplete videos
 */

export const getFirstIndexOfIncompleteVideo = (videos: VideoLesson[]) =>
  videos.findIndex((video: VideoLesson) => video.completionStatus !== 'completed')

/**
 *
 * @param videos
 * @returns returns to see if review activity is available. would return true if every video is completed
 */

export const getReviewAvailability = (videos: VideoLesson[]): boolean =>
  videos.filter((video) => video.completionStatus === 'completed').length === videos.length

export const isAssessmentSummaryStage = (assignment: Assignment): boolean => {
  return (
    assignment?.content?.group == 'assessment' &&
    [
      AssessmentStages.initial,
      AssessmentStages.ongoing,
      AssessmentStages.pre_session,
      AssessmentStages.post_session,
    ].includes((assignment?.content_meta_data?.stage || '') as AssessmentStages)
  )
}

/*
  return response with preferred name
*/
export const isWithPreferredName = (
  isPreferredNameEnabled: boolean,
  preferredFirstName: string | null | undefined,
  preferredLastName: string | null | undefined,
  response?: Response,
  title?: string,
): Response | undefined => {
  if (isPreferredNameEnabled && title?.toLocaleLowerCase().includes('intake')) {
    if (isEmpty(response) && !preferredFirstName && !preferredLastName) return
    const updatedResponse = { ...(response || {}) }

    if (preferredFirstName) {
      updatedResponse.newPreferredFirstName = preferredFirstName
    } else {
      delete updatedResponse.newPreferredFirstName
    }

    if (preferredLastName) {
      updatedResponse.newPreferredLastName = preferredLastName
    } else {
      delete updatedResponse.newPreferredLastName
    }

    return updatedResponse
  }

  return response
}

// get the non-introductory thumbnail if available
export const getThumbnail = (LessonContent: LessonContent[]): string | undefined => {
  if (
    LessonContent &&
    (LessonContent[0]?.name?.includes('introTo') || LessonContent[0]?.name?.includes('introductionTo')) &&
    LessonContent[1]?.meta_data?.thumbnail
  ) {
    return LessonContent[1].meta_data.thumbnail
  }
  return LessonContent?.[0]?.meta_data?.thumbnail
}

/**
 * Determine if a field should be displayed based on the value of its parent field
 */
export const isFieldDisplayed = ({
  condition,
  formValues,
  hidden,
  formFields,
  name = '',
}: IsFieldDisplayedParams): boolean => {
  if (hidden) {
    return false
  }
  if (isNil(condition)) {
    return true
  }
  if (condition.conditionType && condition.items) {
    switch (condition.conditionType) {
      case 'OR':
        return condition.items?.some((condition) => isFieldDisplayed({ condition, formValues, formFields, name }))
      case 'AND':
        return condition.items?.every((condition) => isFieldDisplayed({ condition, formValues, formFields, name }))
      case 'SUM':
        /* If the sum of values for the selected fields is greater than or equal to the threshold value
         * we will not display the field else it will be displayed
         */
        if (condition?.operator === 'LTE') {
          return condition.threshold
            ? condition.items?.reduce((prevValue, currentValue) => {
                const fieldValue = currentValue?.parentField && get(formValues, currentValue.parentField)
                if (isNumber(fieldValue)) {
                  return prevValue + fieldValue
                }
                return Number.MAX_SAFE_INTEGER
              }, 0) <= condition.threshold
            : false
        } else {
          return condition.threshold
            ? condition.items?.reduce((prevValue, currentValue) => {
                const fieldValue = currentValue?.parentField && get(formValues, currentValue.parentField)
                if (isNumber(fieldValue)) {
                  return prevValue + fieldValue
                }
                return Number.MIN_SAFE_INTEGER
              }, 0) >= condition.threshold
            : false
        }
    }
  }

  const parentFieldCondition =
    formFields && condition.parentField
      ? getFieldSchema({ name: condition.parentField, fields: formFields })?.condition
      : undefined
  const parentFieldParentFields = parentFieldCondition?.items
    ? parentFieldCondition.items.map((condition) => condition.parentField)
    : [parentFieldCondition?.parentField]
  const areFieldsInterDependent = parentFieldParentFields.includes(name)
  if (
    condition?.checkVisibility !== false &&
    !areFieldsInterDependent &&
    !isFieldDisplayed({ condition: parentFieldCondition, formValues, formFields, name: condition.parentField ?? '' })
  ) {
    return false
  }
  if (!isNil(condition.parentRegex) && !isNil(condition.parentValue)) {
    throw new Error('You cannot specify both "parentRegex" and "parentValue". it needs to be one or the other.')
  }
  const fieldValue = condition.parentField && get(formValues, condition.parentField)

  if (condition.parentRegex) {
    if (!isString(fieldValue)) {
      return false
    }
    const regex = new RegExp(condition.parentRegex.pattern, condition.parentRegex.flag)
    return regex.test(fieldValue)
  }
  if (isArray(condition.parentValue)) {
    const match = condition.match ?? 'oneOf'
    switch (match) {
      case 'oneOf':
        return condition.parentValue.some((val) => {
          if (isString(fieldValue) || isNumber(fieldValue) || isBoolean(fieldValue)) {
            return fieldValue === val
          } else if (isArray(fieldValue)) {
            // If the field value is an array we should use the intersects match instead
            return fieldValue.includes(val)
          } else {
            return false
          }
        })
      case 'isNot':
        return (
          condition.parentValue.findIndex((val) => {
            if (isString(fieldValue) || isNumber(fieldValue) || isBoolean(fieldValue)) {
              return fieldValue === val
            } else if (isArray(fieldValue)) {
              return fieldValue.includes(val)
            } else {
              return false
            }
          }) === -1
        )
      case 'exact':
        return isEqual(fieldValue, condition.parentValue)
      case 'intersects':
        return isArray(fieldValue) ? !!intersection(fieldValue, condition.parentValue).length : false
    }
  } else {
    return isEqual(fieldValue, condition.parentValue)
  }
}

const isHiddenByPagination = ({
  mobilePage,
  currentPage,
  singlePageContent,
  totalPages,
}: IsHiddenByPaginationParams) => {
  if (isNumber(currentPage) && isNumber(totalPages) && !singlePageContent) {
    if (!isNil(mobilePage)) {
      return mobilePage > currentPage
    } else {
      // If mobile page is undefined then place it on a new page at the end of the activity
      return currentPage < totalPages
    }
  }
  return false
}

export const getVisibleFields = ({ uiSchema, schema, values }: GetVisibleFieldsParams) => {
  return uiSchema?.['ui:order']?.filter((fieldName: string) => {
    const field = schema?.properties?.[fieldName]
    return isFieldDisplayed({
      condition: field?.condition,
      formValues: values || {},
      formFields: schema?.properties,
      name: field?.name,
    })
  })
}

export const validationRules = {
  isRequired,
  characterLimit,
  isValidPastDate,
  isValidPhoneNumber,
  isValidInternationalPhoneNumber,
  isValidEmail,
  isPositiveInteger,
  isValidPastYear,
  hasValue,
  maxValue,
  isValidAge,
  isValidAgeCustomErrors,
  isValid5DigitZipCode,
  isValidFirstName,
  isValidLastName,
  isBooleanTrue,
  isValidPassword,
  isValidCustomerCode,
  isValidWalmartWin,
  rangeHasValidValue,
}

/**
 * Validate form and filter out undisplayed fields
 */
export const validateForm = ({
  fields,
  values,
  errors = {},
  pagination = false,
  currentPage = 0,
  totalPages,
  singlePageContent,
  formatMessage,
  disableIsRequiredFormValidation,
  schema,
}: ValidateFormParams): Dict => {
  if (!fields) {
    return errors
  }
  Object.entries(fields).forEach(([, { name, items, condition, validation, mobilePage, show, customErrorMessage }]) => {
    if (items) {
      const fields = items.reduce(
        (obj: FieldSchema, item: Dict) => ({
          [item.name as string]: { ...item, mobilePage, show: show || item.show },
          ...obj,
        }),
        {},
      )
      return validateForm({ fields, values, errors, currentPage, pagination, totalPages, formatMessage, schema })
    }

    const hiddenByPagination = pagination
      ? isHiddenByPagination({ mobilePage, currentPage, singlePageContent, totalPages })
      : false
    const hiddenByShowProp = show === false
    if (
      !validation ||
      !isFieldDisplayed({
        condition,
        formValues: values,
        formFields: fields,
        hidden: hiddenByPagination || hiddenByShowProp,
        name,
      })
    ) {
      return
    }
    return Object.entries(validation).forEach(([key, validationValue]) => {
      // Each rule takes a differently shaped `validationValue`, so dispatch by key is untyped
      const rule: ((...args: any[]) => Dict) | undefined = validationRules[key as keyof typeof validationRules]
      if ((disableIsRequiredFormValidation ? key !== 'isRequired' : true) && isFunction(rule)) {
        errors = merge(errors, rule(get(values, name), name, validationValue, formatMessage, customErrorMessage))
      }
    })
  })

  if (schema?.schemaValidation?.requireAtLeastOneOf && formatMessage) {
    const fieldsByName = fields as { [key: string]: FieldSchema }
    schema.schemaValidation.requireAtLeastOneOf.forEach((fieldGroup) => {
      const allFieldsVisible = fieldGroup.every((fieldName) => {
        const field = fieldsByName[fieldName]
        if (!field) return false

        const hiddenByPagination = pagination
          ? isHiddenByPagination({ mobilePage: field.mobilePage, currentPage, singlePageContent, totalPages })
          : false
        const hiddenByShowProp = field.show === false

        return isFieldDisplayed({
          condition: field.condition,
          formValues: values,
          formFields: fields,
          hidden: hiddenByPagination || hiddenByShowProp,
          name: fieldName,
        })
      })

      if (allFieldsVisible) {
        const fieldTitles = fieldGroup
          .map((fieldName) => fieldsByName[fieldName]?.title)
          .filter((title): title is string => typeof title === 'string')
        errors = merge(errors, requireAtLeastOneOf(fieldGroup, values, formatMessage, fieldTitles))
      }
    })
  }

  return errors
}

/**
 * Transforms nested objects into a flat object with dot notation.
 * For example: { foo: { bar: 1 }} would become { 'foo.bar': 1}
 */
const flattenObject = (obj: Dict = {}, prefix = ''): Dict => {
  obj = obj ?? {}
  return Object.keys(obj).reduce<Dict>((acc, k) => {
    const pre = prefix.length ? prefix + '.' : ''
    const value = obj[k]
    if (typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(acc, flattenObject(value as Dict, pre + k))
    } else {
      acc[pre + k] = value
    }
    return acc
  }, {})
}

/**
 *  Get a specific field's metada from its name
 */
export const getFieldSchema = ({ name, fields, fieldSchema = [] }: GetFieldSchemaParams): FieldSchema | undefined => {
  Object.entries(fields).forEach(([fieldName, schema]) => {
    if (fieldName === name) {
      fieldSchema.push(schema)
    } else if (schema.items) {
      // Transform `items` array to an object
      const subFields = schema.items.reduce(
        (obj: Dict, item: FieldSchema) => ({ [item.name as string]: item, ...obj }),
        {},
      ) as SchemaProperties
      getFieldSchema({ name, fields: subFields, fieldSchema })
    }
  })
  return fieldSchema[0]
}

/**
 * Given a note get all section headers
 */
export const getNoteSectionHeaders = ({
  note,
  fields,
  noteValues,
  externalValues = {},
  headers = [],
}: {
  note: NoteMetadata
  fields: SchemaProperties
  noteValues: object
  externalValues?: { [key: string]: FieldValue }
  headers?: FieldSchema[]
}) => {
  const uiSchemaOrder = note?.meta_data?.uiSchema?.['ui:order'] as unknown as string[]

  uiSchemaOrder &&
    uiSchemaOrder.forEach((field) => {
      if (fields[field]?.sectionHeader) {
        const fieldSchema = getFieldSchema({ name: field, fields })
        if (isFieldDisplayed({ condition: fieldSchema?.condition, formValues: { ...noteValues, ...externalValues } })) {
          return headers.push({ ...fields[field], name: field })
        } else {
          return
        }
      } else if (fields[field]?.items) {
        // Transform `items` array to an object
        const subFields = fields[field]?.items?.reduce((obj, item) => ({ [item.name as string]: item, ...obj }), {})
        return subFields
          ? getNoteSectionHeaders({ note, fields: subFields as SchemaProperties, noteValues, headers, externalValues })
          : []
      } else {
        return []
      }
    })
  return headers
}

/**
 * Given a note get all section headers
 */
export const getAssessmentSectionHeaders = ({
  assignment,
  fields,
  formValues,
  headers = [],
  headerDesignation,
}: {
  assignment?: Assignment
  fields: SchemaProperties
  formValues: object | Response
  headers?: FieldSchema[]
  headerDesignation?: string
}) => {
  const uiSchemaOrder = assignment?.content_meta_data?.uiSchema?.['ui:order'] as unknown as string[]

  if (isNil(headerDesignation)) {
    headerDesignation =
      uiSchemaOrder &&
      uiSchemaOrder.find((field) => {
        const fieldContent = fields[field]?.content as string
        return fieldContent && fieldContent.startsWith('## ')
      })
        ? '##'
        : '####'
  }

  uiSchemaOrder &&
    uiSchemaOrder.forEach((field) => {
      const fieldContent = fields[field]?.content as string
      const isSectionHeader = fieldContent && fieldContent.startsWith(`${headerDesignation ?? '####'} `)
      if (isSectionHeader) {
        const fieldSchema = getFieldSchema({ name: field, fields })
        if (isFieldDisplayed({ condition: fieldSchema?.condition, formValues: { ...formValues } })) {
          return headers.push({ ...fields[field], content: fieldContent.split(headerDesignation ?? '####')[1].trim() })
        } else {
          return
        }
      } else if (fields[field]?.items) {
        // Transform `items` array to an object
        const subFields = fields[field]?.items?.reduce((obj, item) => ({ [item.name as string]: item, ...obj }), {})
        return subFields
          ? getAssessmentSectionHeaders({
              assignment,
              fields: subFields as SchemaProperties,
              formValues,
              headers,
              headerDesignation,
            })
          : []
      } else {
        return []
      }
    })
  return headers
}

/**
 * Given a note get all AI enabled fields
 */
export const getAIEnabledFields = ({
  note,
  fields,
  noteValues,
  aiEnabledFields = [],
}: {
  note: NoteMetadata
  fields: SchemaProperties
  noteValues: object
  aiEnabledFields?: Partial<FieldSchema>[]
}) => {
  const uiSchemaOrder = note?.meta_data?.uiSchema?.['ui:order'] as unknown as string[]

  uiSchemaOrder &&
    uiSchemaOrder.forEach((field) => {
      if (!fields[field]?.sectionHeader && fields[field]?.aiPromptEnabled) {
        const fieldSchema = getFieldSchema({ name: field, fields })
        if (isFieldDisplayed({ condition: fieldSchema?.condition, formValues: { ...noteValues } })) {
          return aiEnabledFields.push({ ...fields[field], name: field })
        } else {
          return
        }
      } else {
        return []
      }
    })
  return aiEnabledFields
}

/**
 *  Extend metadata
 */
export const extendMetadata = (content1: FormMetadata, content2: FormMetadata) => {
  const content = cloneDeep(content1)
  content.schema.properties = {
    ...content1.schema.properties,
    ...content2.schema.properties,
  }
  content.uiSchema = { ...content1.uiSchema, ...content2.uiSchema }
  content.uiSchema['ui:order'] = [...(content1.uiSchema['ui:order'] ?? []), ...(content2.uiSchema['ui:order'] ?? [])]

  content.initialValues = { ...(content1.initialValues ?? {}), ...(content2.initialValues ?? {}) }
  return content
}

/**
 *  Return a new values object with not visible fields filtered out
 */
export const removeHiddenFields = ({
  values,
  fields,
  externalValues,
}: RemoveHiddenFieldsParams): { [key: string]: FieldValue } => {
  if (fields) {
    const fieldNames = Object.keys(flattenObject(values))
    fieldNames.forEach((fieldName) => {
      const fieldSchema = getFieldSchema({ name: fieldName, fields })
      if (fieldSchema) {
        if (!isFieldDisplayed({ condition: fieldSchema.condition, formValues: { ...values, ...externalValues } })) {
          set(values, fieldName, undefined)
        }
      }
    })
  }
  return values
}

export const convertToInternationalFormat = (fieldValue: string, countryCode: [string]) => {
  if (!isEmpty(fieldValue) && !isEmpty(countryCode)) {
    const libPhoneNumber = parsePhoneNumber(fieldValue as string, countryCode[0] as CountryCode)
    return libPhoneNumber?.number ?? fieldValue
  }
  return fieldValue
}

export const convertToNationalFormat = (storedValue: string, countryCode: [string]) => {
  if (!isEmpty(storedValue) && typeof storedValue === 'string' && !isEmpty(countryCode)) {
    const libPhoneNumber = parsePhoneNumber(storedValue as string, countryCode[0] as CountryCode)
    return libPhoneNumber?.nationalNumber ?? storedValue
  }
  return storedValue
}

export const parseFunctions = {
  convertToInternationalFormat,
}

export const formatFunctions = {
  convertToNationalFormat,
}

/**
 *  Return the highest page number from response data
 */
export const getLastPageFromResponse = ({ response, schema }: getLastPageFromResponseParams): number => {
  if (isNil(response) || isNil(schema)) {
    return 0
  }
  const properties = schema.properties
  if (isEmpty(response) || !properties) {
    return 0
  }
  const pageNumbers = Object.keys(response).map((fieldName) => {
    const fieldSchema = getFieldSchema({ name: fieldName, fields: properties })
    if (!isNil(fieldSchema?.mobilePage)) {
      return fieldSchema?.mobilePage
    } else {
      // If fieldSchema does not have mobile page check to see if it's parent does
      const parentArrayKey = Object.keys(properties).find((propertyName) =>
        properties[propertyName]?.items?.some((item?: FieldSchema) => item?.name === fieldSchema?.name),
      )
      return parentArrayKey ? properties[parentArrayKey]?.mobilePage : 0
    }
  })
  const filterPageNumbers = pageNumbers.filter((element) => element !== undefined) as number[]
  return Math.max(...filterPageNumbers)
}

/**
 * Resume at first page with missing required fields if user progressed past it,
 * otherwise resume at last page with values. Prevents getting stuck after clearing required fields.
 */
export const getResumePageFromResponse = ({
  response,
  schema,
  totalPages,
}: getResumePageFromResponseParams): number => {
  if (isNil(response) || isNil(schema) || isNil(totalPages)) {
    return 0
  }

  const properties = schema.properties
  if (!properties) {
    return 0
  }

  const lastPageWithValues = getLastPageFromResponse({ response, schema })

  // Check for missing required fields on pages before the last filled page
  for (let page = 0; page < lastPageWithValues; page++) {
    if (
      checkPageForMissingRequiredFields({
        fields: properties,
        values: response as { [key: string]: FieldValue },
        currentPage: page,
        totalPages,
      })
    ) {
      return page
    }
  }

  return lastPageWithValues
}

/**
 * Check if a page has missing required fields
 */
const checkPageForMissingRequiredFields = ({
  fields,
  values,
  currentPage,
  totalPages,
}: {
  fields: { [key: string]: FieldSchema } | FieldSchema
  values: { [key: string]: FieldValue }
  currentPage: number
  totalPages: number
}): boolean => {
  if (!fields) {
    return false
  }

  for (const [, field] of Object.entries(fields)) {
    const { name, items, condition, validation, mobilePage, show } = field

    // Handle nested fields
    if (items) {
      const nestedFields = items.reduce(
        (obj: FieldSchema, item: Dict) => ({
          [item.name as string]: { ...item, mobilePage, show: show || item.show },
          ...obj,
        }),
        {},
      )
      if (
        checkPageForMissingRequiredFields({
          fields: nestedFields,
          values,
          currentPage,
          totalPages,
        })
      ) {
        return true
      }
      continue
    }

    // Skip if not on current page or hidden
    // Note: isHiddenByPagination returns true for fields on later pages (mobilePage > currentPage),
    // which correctly filters out fields that aren't on the page we're checking
    if (
      isHiddenByPagination({ mobilePage, currentPage, singlePageContent: false, totalPages }) ||
      show === false ||
      !isFieldDisplayed({
        condition,
        formValues: values,
        formFields: fields,
        hidden: false,
        name,
      })
    ) {
      continue
    }

    // Check if required field is missing
    if (validation?.isRequired) {
      const fieldValue = get(values, name)
      // Check for empty values: null, undefined, empty strings/arrays, or Immutable.js objects with isEmpty()
      const isMissing =
        (!isBoolean(fieldValue) && !isNumber(fieldValue) && isEmpty(fieldValue)) ||
        (typeof (fieldValue as any)?.isEmpty === 'function' && (fieldValue as any).isEmpty())

      if (isMissing) {
        return true
      }
    }
  }

  return false
}

/**
 * Helper function to determine if the To-Do badge should be shown for current assignments
 */
export const shouldShowToDo = ({ group, hasCompletedResponse, status }: shouldShowToDoParams) => {
  switch (group) {
    case AssignmentGroup.ASSESSMENT:
      return !includes([AssignmentStatuses.completed, AssignmentStatuses.draft, AssignmentStatuses.missed], status)
    case AssignmentGroup.EXERCISE:
      return !hasCompletedResponse
    default:
      return status !== AssignmentStatuses.completed
  }
}

export const isAssignmentHidden = ({
  currentAssignment,
  assignments,
}: {
  currentAssignment?: Assignment
  assignments?: Assignment[]
}): boolean => {
  if (isNil(currentAssignment) || isNil(assignments)) {
    return false
  }

  const shouldBeHidden = customContentAttributes[currentAssignment.content.name]?.hideInClientActivities

  if (shouldBeHidden) {
    const shownParentActivityNameValue = customContentAttributes[currentAssignment.content.name]?.shownParentActivity
    const shownParentActivity = assignments.find((assignment) => {
      return (
        (typeof shownParentActivityNameValue === 'string'
          ? assignment.content.name === shownParentActivityNameValue
          : shownParentActivityNameValue?.includes(assignment.content.name)) &&
        assignment.episode_id === currentAssignment.episode_id &&
        assignment.status !== AssignmentStatuses.missed
      )
    })
    return !isNil(shownParentActivity)
  }

  return false
}

export const isAssessmentCurrent = ({
  assessment,
  assignments,
  isActiveEpisode,
  currentAppointment,
  isCurrentPeriod,
}: isAssessmentCurrentParams) => {
  // All new, draft or in-progress assessments are current
  if (
    !includes(
      [AssignmentStatuses.completed, AssignmentStatuses.partially_completed, AssignmentStatuses.missed],
      getCombinedAssignmentStatus({ assignment: assessment, assignments }),
    )
  ) {
    return true
    // All assessments associated with an inactive episode or past session period are past
  } else if (!isActiveEpisode || !isCurrentPeriod) {
    return false
  } else if (currentAppointment) {
    const updateDate = moment.utc(assessment.update_date)
    const submitDate = moment.utc(assessment.assignment_responses?.[0]?.submit_date)
    const sessionStartDate = getAppointmentDateTimeObject(currentAppointment)
    // If it's after the appointment date then assessment is past, otherwise it's current
    // Use submitDate for completed assessments and update for non-completed
    if (assessment.status === 'completed') {
      return submitDate.isSameOrAfter(sessionStartDate)
    } else {
      // Note: this may not be needed any more because missed assessments are hidden
      return updateDate.isSameOrAfter(sessionStartDate)
    }
  } else {
    // Fall back to current
    return true
  }
}

export const assignmentHasNonCompletedProviderDraft = (assignment?: Assignment | EpisodeContents) => {
  if (assignment?.id) {
    return (
      assignmentOnlyHasProviderDraft(assignment as Assignment) &&
      (assignment as Assignment)?.assignment_responses?.[0]?.status !== AssignmentStatuses.completed
    )
  } else {
    return false
  }
}

export const assignmentHasProviderDraft = (assignment?: Assignment) => {
  return assignment?.assignment_responses?.some((response: AssignmentResponse) => {
    return response.provider_started
  })
}

// get the first provider draft response
export const getProviderDraftResponse = (assignment?: Assignment) => {
  return assignment?.assignment_responses?.find((response: AssignmentResponse) => {
    return response.provider_started
  })
}

export const assignmentOnlyHasProviderDraft = (assignment?: Assignment) => {
  return assignment?.assignment_responses?.length === 1 && assignmentHasProviderDraft(assignment)
}

export const assignmentHasCompletedResponse = (assignment?: Assignment) => {
  return assignment?.assignment_responses?.some((response: AssignmentResponse) => {
    return response.status === 'completed'
  })
}

export const getEffectiveRecipientLyraId = (recipientLyraId?: string | null, patientId?: string): string | undefined =>
  recipientLyraId || patientId

export const removeFutureSessionExercisesFromActivities = (assignments: Assignment[], providers: Provider[]) => {
  return assignments.filter((assignment) => {
    const { episode_id, provider_id, session_period } = assignment
    const sessionPeriod = session_period === 0 ? session_period + 1 : session_period
    const currentEpisodeSessionPeriod = getProviderSessionCounts(provider_id, episode_id, providers)
    const isFutureSessionExercise =
      assignment.content.group === AssignmentGroup.EXERCISE && sessionPeriod > currentEpisodeSessionPeriod

    return !isFutureSessionExercise
  })
}

// get if the pre/postrequisite assignment should display when going through the assignments. Only either pre or post requisite should be passed
export const getShownLinkedAssignment = ({
  clickedAssignment,
  assignments,
}: {
  clickedAssignment?: Assignment
  assignments?: Assignment[]
}): Assignment | undefined => {
  if (isNil(clickedAssignment)) {
    return
  }
  if (isNil(assignments)) {
    return clickedAssignment
  }

  const linkedAssignments = getLinkedAssignments({
    selectedAssignment: clickedAssignment,
    assignments,
  })

  const firstOpenAssignment = linkedAssignments.find(
    (assignment) =>
      ![AssignmentStatuses.completed, AssignmentStatuses.missed].includes(
        assignment.status as string as AssignmentStatuses,
      ),
  )

  if (firstOpenAssignment) {
    return firstOpenAssignment
  }

  const firstCompletedAssignment = linkedAssignments.find(
    (assignment) => assignment.status === AssignmentStatuses.completed,
  )

  if (firstCompletedAssignment) {
    return firstCompletedAssignment
  }

  return clickedAssignment
}

export const getClientPreferredNameIfExists = (client: ClientObject | null | undefined) => {
  return client?.preferred_first_name || client?.preferred_last_name
    ? (client?.preferred_first_name ?? client?.first_name) + ' ' + (client?.preferred_last_name ?? client?.last_name)
    : ''
}

export const getPreAndPostRequisites = ({
  selectedAssignment,
  assignments,
}: {
  selectedAssignment?: Assignment | null
  assignments?: Assignment[]
}) => {
  const preAndPostRequisites: { prerequisite: Assignment | undefined; postrequisite: Assignment | undefined } = {
    prerequisite: undefined,
    postrequisite: undefined,
  }
  if (isNil(selectedAssignment) || !assignments) {
    return preAndPostRequisites
  }

  const linkedAssignments = getLinkedAssignments({
    selectedAssignment: selectedAssignment,
    assignments,
  })

  const selectedAssignmentName = selectedAssignment.content.name

  const currentAssignmentIndex = linkedAssignments.findIndex(
    (linkedAssignment) => linkedAssignment.content.name === selectedAssignmentName,
  )

  const linkedVideo = customContentAttributes[selectedAssignment.content.name]?.linkedVideo

  if (currentAssignmentIndex > 0) {
    preAndPostRequisites.prerequisite = linkedAssignments[currentAssignmentIndex - 1]
  }

  if (currentAssignmentIndex < linkedAssignments.length - 1) {
    preAndPostRequisites.postrequisite = linkedAssignments[currentAssignmentIndex + 1]
  } else if (currentAssignmentIndex === linkedAssignments.length - 1 && linkedVideo) {
    preAndPostRequisites.postrequisite = assignments.find((assignment) => assignment.content.name === linkedVideo)
  }
  return preAndPostRequisites
}

export const getAssignmentStatusLabel = ({
  formatMessage,
  status,
  simplifyStatus = false,
}: {
  formatMessage?: IntlShape['formatMessage']
  status?: AssignmentStatuses
  simplifyStatus?: boolean
}): string => {
  if (isNil(status)) {
    return ''
  }

  if (simplifyStatus && formatMessage) {
    switch (status) {
      case AssignmentStatuses.new:
        return formatMessage({
          defaultMessage: 'Not started',
          description: 'The assignment has not yet been started',
        })
      case AssignmentStatuses.in_progress:
      case AssignmentStatuses.draft:
      case AssignmentStatuses.provider_draft:
        return formatMessage({
          defaultMessage: 'In progress',
          description: 'The assignment has been started and is in progress',
        })
      case AssignmentStatuses.completed:
        return formatMessage({
          defaultMessage: 'Completed',
          description: 'The assignment has been completed',
        })
      case AssignmentStatuses.partially_completed:
        return formatMessage({
          defaultMessage: 'Partially completed',
          description: 'The assignment has been partially completed',
        })
      case AssignmentStatuses.missed:
        return formatMessage({
          defaultMessage: 'Missed',
          description: 'The assignment was missed and there is no entry for it',
        })
      default:
        return ''
    }
  }
  return AssignmentStatusesLabel[status] ?? ''
}

export const getLinkedAssignments = ({
  selectedAssignment,
  assignments,
  includeMissedAssignments,
}: {
  selectedAssignment?: Assignment | null
  assignments: Assignment[]
  includeMissedAssignments?: boolean
}): Assignment[] => {
  if (isNil(selectedAssignment)) {
    return []
  }
  const linkedStatusAssignmentsAttribute =
    customContentAttributes[selectedAssignment.content.name]?.linkedStatusAssignments

  if (isNil(linkedStatusAssignmentsAttribute) || isNil(assignments)) {
    return [selectedAssignment]
  }

  const linkedAssignments: Assignment[] = []
  linkedStatusAssignmentsAttribute?.forEach((assignmentName: string | string[]) => {
    if (
      typeof assignmentName === 'string'
        ? assignmentName === selectedAssignment.content.name
        : assignmentName.includes(selectedAssignment.content.name)
    ) {
      linkedAssignments.push(selectedAssignment)
    } else {
      const linkedAssignment = assignments?.find(
        (asgmt) =>
          (typeof assignmentName === 'string'
            ? assignmentName === asgmt.content.name
            : assignmentName.includes(asgmt.content.name)) &&
          asgmt.episode_id === selectedAssignment.episode_id &&
          (includeMissedAssignments || asgmt.status !== AssignmentStatuses.missed),
      )
      if (linkedAssignment) {
        linkedAssignments.push(linkedAssignment)
      }
    }
  })

  return linkedAssignments
}

export const getCombinedAssignmentCompletionDate = ({
  assignment,
  assignments,
}: {
  assignment?: Assignment
  assignments?: Assignment[]
}): any => {
  if (isNil(assignment)) {
    return
  }

  if (!(assignment.content.name in customContentAttributes)) {
    return assignment?.assignment_responses?.[0]?.submit_date
  }

  if (isNil(assignments)) {
    const submitDate = assignment?.assignment_responses?.[0]?.submit_date
    if (submitDate) {
      return new Date(submitDate)
    }
    return
  }

  const completionDates: Date[] = []
  const linkedAssignments = getLinkedAssignments({ selectedAssignment: assignment, assignments })
  linkedAssignments.forEach((assignment: Assignment) => {
    const submitDate = assignment?.assignment_responses?.[0]?.submit_date
    if (submitDate) {
      completionDates.push(new Date(submitDate))
    }
  })
  return completionDates.length ? max(completionDates) : undefined
}

export const getCombinedAssignmentStatus = ({
  assignment,
  assignments,
}: {
  assignment?: Assignment
  assignments?: Assignment[]
}): any => {
  if (isNil(assignment)) {
    return
  }

  if (!(assignment.content.name in customContentAttributes)) {
    return assignment?.assignment_responses?.[0]?.status ?? assignment.status
  }

  if (isNil(assignments)) {
    return (assignment?.assignment_responses?.[0]?.status ?? assignment.status) as string as AssignmentStatuses
  }

  const completionStatuses: AssignmentStatuses[] = []

  const linkedAssignments = getLinkedAssignments({
    selectedAssignment: assignment,
    assignments,
    includeMissedAssignments: true,
  })
  linkedAssignments.forEach((assignment: Assignment) => {
    completionStatuses.push(
      (assignment?.assignment_responses?.[0]?.status ?? assignment.status) as string as AssignmentStatuses,
    )
  })

  if (completionStatuses.length === 1) {
    return completionStatuses[0]
  }

  if (
    completionStatuses.some((status) =>
      [AssignmentStatuses.in_progress, AssignmentStatuses.draft, AssignmentStatuses.provider_draft].includes(status),
    )
  ) {
    return AssignmentStatuses.in_progress
  }

  if (completionStatuses.every((status) => [AssignmentStatuses.new].includes(status))) {
    return AssignmentStatuses.new
  }

  if (
    completionStatuses.some((status) => [AssignmentStatuses.new].includes(status)) &&
    completionStatuses.some((status) => ![AssignmentStatuses.new].includes(status))
  ) {
    return AssignmentStatuses.in_progress
  }

  if (completionStatuses.every((status) => [AssignmentStatuses.completed].includes(status))) {
    return AssignmentStatuses.completed
  }

  if (completionStatuses.some((status) => [AssignmentStatuses.completed].includes(status))) {
    return AssignmentStatuses.partially_completed
  }

  if (completionStatuses.every((status) => [AssignmentStatuses.missed].includes(status))) {
    return AssignmentStatuses.missed
  }

  return AssignmentStatuses.in_progress
}

export const getIsClientIntake = (assignmentName?: string) => {
  if (isNil(assignmentName)) {
    return false
  }
  const regex = /clientintake/i
  return regex.test(assignmentName)
}

export const getIsRCADS25TeenAssessment = (assignmentName?: string) => {
  if (isNil(assignmentName)) {
    return false
  }
  return rcads25TeenAssessmentsDBTT.includes(assignmentName)
}

export const getIsRCADSP25Assessment = (assignmentName?: string) => {
  if (isNil(assignmentName)) {
    return false
  }
  return rcadsp25AssessmentsDBTT.includes(assignmentName) || assignmentName === 'daRCADSP25Assessment'
}

export const getIsAssignmentGoogleActionPlan = (assignment: Assignment | null) => {
  if (isNil(assignment)) {
    return false
  }
  return assignment.content.group === AssignmentGroup.EXERCISE && assignment.content.name === 'baseActionPlan'
}

// Action Plans are excluded from per-session status updates. This returns the open Action Plan
// assignments for an episode that should be marked completed at episode end.
export const getOpenActionPlansForEpisode = (
  assignments: Assignment[] | undefined,
  episodeId: string | undefined,
): Assignment[] =>
  (assignments ?? []).filter(
    (assignment) =>
      getIsAssignmentGoogleActionPlan(assignment) &&
      assignment.episode_id === episodeId &&
      !([AssignmentStatuses.completed, AssignmentStatuses.missed] as string[]).includes(assignment.status),
  )

export const sortEpisodeDropdownData = (
  a: ClientProgramDropdownData | DependentProgramDropdownData,
  b: ClientProgramDropdownData | DependentProgramDropdownData,
  sortKey: 'earliestAppointmentDate' | 'episodeEndDate' | 'upcomingAppointmentDate',
  isAscending: boolean = true,
) => {
  const aValue = a[sortKey as keyof typeof a]
  const bValue = b[sortKey as keyof typeof b]

  if (aValue != null && bValue == null) return -1
  if (aValue == null && bValue != null) return 1
  if (aValue == null && bValue == null) return 0

  if (aValue === bValue) {
    const aId = 'episodeId' in a ? a.episodeId : ''
    const bId = 'episodeId' in b ? b.episodeId : ''
    return aId.localeCompare(bId)
  }

  if (isAscending) {
    return (aValue as Date).getTime() - (bValue as Date).getTime()
  } else {
    return (bValue as Date).getTime() - (aValue as Date).getTime()
  }
}

export const shouldShowClientProgramDropdown = (dropdownData: ClientProgramDropdownViewModel): boolean => {
  // Count each dependent's episodes rather than the dependents themselves, so a single child with
  // more than one active episode still gets a switcher to move between them.
  const dependentEpisodes = dropdownData.childOrTeenDependents.flatMap(({ episodes }) => episodes)
  const hasMultiplePrograms =
    dropdownData.activeEpisodes.length + dropdownData.graduatedEpisodes.length + dependentEpisodes.length > 1

  if (!hasMultiplePrograms) return false

  const hasOnlyFindCareForYourself =
    dropdownData.activeEpisodes.length === 1 &&
    dropdownData.activeEpisodes[0].episodeId === CLIENT_PROGRAM_DROPDOWN_DUMMY_EPISODE_ID &&
    dropdownData.graduatedEpisodes.length === 0
  const hasOneDbtTeensEpisode =
    new Set(dependentEpisodes.map(({ episodeId }) => episodeId)).size === 1 &&
    dependentEpisodes.every(({ programName }) => programName === ProgramNames.DialecticalBehaviorTherapyTeens)

  return !(hasOnlyFindCareForYourself && hasOneDbtTeensEpisode)
}

type IsFieldDisplayedParams = {
  condition?: Condition
  formValues: { [key: string]: FieldValue } | Response
  hidden?: boolean
  formFields?:
    | {
        [key: string]: FieldSchema
      }
    | FieldSchema
    | undefined
  name?: string
}

type IsHiddenByPaginationParams = {
  mobilePage?: number
  currentPage?: number
  totalPages?: number
  singlePageContent?: boolean
}

type GetVisibleFieldsParams = {
  uiSchema?: UiMetadata
  schema?: FieldSchema
  values?: { [key: string]: FieldValue }
}

type ValidateFormParams = {
  fields:
    | {
        [key: string]: FieldSchema
      }
    | FieldSchema
    | undefined
  values: { [key: string]: FieldValue }
  errors?: Dict
  currentPage?: number
  totalPages?: number
  singlePageContent?: boolean
  pagination?: boolean
  formatMessage?: IntlShape['formatMessage']
  disableIsRequiredFormValidation?: boolean
  schema?: FieldSchema
}

type GetFieldSchemaParams = {
  name: string
  fields:
    | {
        [key: string]: FieldSchema
      }
    | FieldSchema
  fieldSchema?: FieldSchema[]
}

type RemoveHiddenFieldsParams = {
  values: { [key: string]: FieldValue }
  fields?: SchemaProperties
  externalValues?: { [key: string]: FieldValue }
}

type getLastPageFromResponseParams = {
  response?: Dict
  schema?: FieldSchema
}

type getResumePageFromResponseParams = {
  response?: Dict
  schema?: FieldSchema
  totalPages?: number
}

type shouldShowToDoParams = {
  group: ActivityGroupType
  hasCompletedResponse?: boolean
  status: string
}

type isAssessmentCurrentParams = {
  assignments?: Assignment[]
  assessment: Assignment
  isActiveEpisode: boolean
  currentAppointment: Appointment | null
  isCurrentPeriod: boolean
}
